import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { merge } from 'lodash';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Platform, getPlatform } from '../util/platform';
import { VERSION } from '../version';
import { createApolloClient } from './apollo.client';
import { JwtProvider, useJwtProvider } from './auth.hooks';
import { AttachmentCacheTypePolicies } from './cache/attachment.cache';
import { ChartCacheTypePolicies } from './cache/chart.cache';
import { ChatCacheTypePolicies } from './cache/chat.cache';
import { EventNotificationCacheTypePolicies } from './cache/event-notification.cache';
import { FeatureCacheTypePolicies } from './cache/feature.cache';
import { MachineCacheTypePolicies } from './cache/machine.cache';
import { PlanCacheTypePolicies } from './cache/plan.cache';
import { PreferenceCacheTypePolicies } from './cache/preference.cache';
import { RoleCacheTypePolicies } from './cache/role.cache';
import { ScratchpadCacheTypePolicies } from './cache/scratchpad.cache';
import { TeamCacheTypePolicies } from './cache/team.cache';
import { UserCacheTypePolicies } from './cache/user.cache';
import { ApolloSubscriptionManagerProvider } from './subscription/apollo-subscription.manager';

export interface ApiProviderProps {
  apollo: {
    uri: string;
  };
  children: ReactNode;
}

interface AuthorizationHeaders {
  authorization: string;
  'x-tenant-id': string;
}

interface CommonHeaders {
  'x-beacon-platform': Platform;
  'x-beacon-client-version': string;
}

const platform = getPlatform();

const getAuthorizationHeaders = async (
  jwtProvider: JwtProvider
): Promise<AuthorizationHeaders | undefined> => {
  const jwtResponse = await jwtProvider();

  if (jwtResponse) {
    return {
      authorization: jwtResponse.token ? `Bearer ${jwtResponse.token}` : '',
      'x-tenant-id': jwtResponse.tenantId as string,
    };
  }
};

export const getCommonApiHeaders = (): CommonHeaders => {
  const headers: CommonHeaders = {
    'x-beacon-platform': platform,
    'x-beacon-client-version': VERSION,
  };

  return headers;
};

const createCache = async (): Promise<InMemoryCache> => {
  return new InMemoryCache({
    typePolicies: merge(
      AttachmentCacheTypePolicies,
      ChartCacheTypePolicies,
      ChatCacheTypePolicies,
      EventNotificationCacheTypePolicies,
      FeatureCacheTypePolicies,
      MachineCacheTypePolicies,
      PlanCacheTypePolicies,
      PreferenceCacheTypePolicies,
      RoleCacheTypePolicies,
      ScratchpadCacheTypePolicies,
      TeamCacheTypePolicies,
      UserCacheTypePolicies
    ),
  });
};

export const CloudApiProvider = (
  props: ApiProviderProps
): ReactElement | null => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [apolloClient, setApolloClient] =
    useState<ApolloClient<NormalizedCacheObject>>();

  const jwtProvider = useJwtProvider({ uri: props.apollo.uri });

  useEffect(() => {
    (async () => {
      const client = await createApolloClient({
        url: props.apollo.uri,
        cacheProvider: async () => {
          const cache = await createCache();

          return {
            cache,
            cacheName: `apollo-cache`,
          };
        },
        headerProvider: async () => {
          const authHeaders = await getAuthorizationHeaders(jwtProvider);
          const commonHeaders = getCommonApiHeaders();

          return {
            ...commonHeaders,
            ...authHeaders,
          };
        },
      });
      setApolloClient(client);
      setIsInitialized(true);
    })();
  }, [props.apollo.uri, jwtProvider]);

  if (isInitialized && apolloClient) {
    return (
      <ApolloProvider client={apolloClient}>
        <ApolloSubscriptionManagerProvider>
          {props.children}
        </ApolloSubscriptionManagerProvider>
      </ApolloProvider>
    );
  }

  return null;
};
